import { z } from 'zod'

import type { ProjectSettings } from './project-settings'
import { TaskStatusTypeEnum } from './task-status'

// Project status enum
export enum ProjectStatusEnum {
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED',
  ON_HOLD = 'ON_HOLD',
  CANCELED = 'CANCELED',
}

// Map project status to canonical status type
export const PROJECT_STATUS_TYPE: Record<
  ProjectStatusEnum,
  TaskStatusTypeEnum
> = {
  [ProjectStatusEnum.ACTIVE]: TaskStatusTypeEnum.IN_PROGRESS,
  [ProjectStatusEnum.COMPLETED]: TaskStatusTypeEnum.COMPLETED,
  [ProjectStatusEnum.ON_HOLD]: TaskStatusTypeEnum.ON_HOLD,
  [ProjectStatusEnum.CANCELED]: TaskStatusTypeEnum.CANCELED,
}

// Project colors — same palette as TaskGroup colors so users get a
// consistent color-picking experience across modules.
export const ProjectColors = [
  '#316bb4',
  '#2a5c9a',
  '#24507f',
  '#1e4369',
  '#475569',
  '#6b7280',
  '#78716c',
  '#52525b',
  '#b91c1c',
  '#dc2626',
  '#ef4444',
  '#f87171',
  '#c2410c',
  '#ea580c',
  '#f97316',
  '#fb923c',
  '#a16207',
  '#ca8a04',
  '#eab308',
  '#facc15',
  '#4d7c0f',
  '#65a30d',
  '#84cc16',
  '#a3e635',
  '#15803d',
  '#16a34a',
  '#22c55e',
  '#4ade80',
  '#0f766e',
  '#0d9488',
  '#14b8a6',
  '#2dd4bf',
  '#155e75',
  '#0891b2',
  '#06b6d4',
  '#22d3ee',
  '#0369a1',
  '#0284c7',
  '#0ea5e9',
  '#38bdf8',
  '#4338ca',
  '#4f46e5',
  '#6366f1',
  '#818cf8',
  '#6d28d9',
  '#7c3aed',
  '#8b5cf6',
  '#a78bfa',
  '#a21caf',
  '#c026d3',
  '#d946ef',
  '#e879f9',
  '#be185d',
  '#db2777',
  '#ec4899',
  '#f472b6',
] as const

export const DEFAULT_PROJECT_COLOR = ProjectColors[0]

// Project status colors - using app design system colors
export const PROJECT_STATUS_COLORS: Record<ProjectStatusEnum, string> = {
  [ProjectStatusEnum.ACTIVE]: '#2c59c3', // Eggshell Indigo (blue)
  [ProjectStatusEnum.COMPLETED]: '#1ead5b', // Herbed Frittata (green)
  [ProjectStatusEnum.ON_HOLD]: '#f48a2f', // Free Range (orange)
  [ProjectStatusEnum.CANCELED]: '#d03b3c', // Paprika (red)
}

// Project status display names
export const PROJECT_STATUS_DISPLAY_NAMES: Record<ProjectStatusEnum, string> = {
  [ProjectStatusEnum.ACTIVE]: 'Active',
  [ProjectStatusEnum.COMPLETED]: 'Completed',
  [ProjectStatusEnum.ON_HOLD]: 'On Hold',
  [ProjectStatusEnum.CANCELED]: 'Canceled',
}

// Helper function to get project status info
export function getProjectStatusInfo(status: ProjectStatusEnum) {
  return {
    name: PROJECT_STATUS_DISPLAY_NAMES[status],
    color: PROJECT_STATUS_COLORS[status],
  }
}

// =====================
// Project lifecycle state
// =====================
//
// `Project.status` is stored as a free-form string (the import path normalizes
// it to one of `'not-started' | 'active' | 'on-hold' | 'completed' | 'canceled' | 'archived'`,
// but legacy uppercase `ProjectStatusEnum` values still appear in some flows).
// `ProjectLifecycleState` is the small, semantic state set that downstream
// systems (allocation auto-archive, workload roll-ups, "active projects"
// filters, etc.) actually care about.
//
// Map status strings to a lifecycle state via `getProjectLifecycleState` and
// then branch on the state, never on the raw string.

export enum ProjectLifecycleState {
  /** Work is happening or about to happen. */
  ACTIVE = 'ACTIVE',
  /** Reversible pause — allocations and reporting should treat as active. */
  ON_HOLD = 'ON_HOLD',
  /** Terminal — completed, canceled, or archived. Drives allocation auto-archive. */
  CLOSED = 'CLOSED',
}

const PROJECT_STATUS_LIFECYCLE_MAP: Record<string, ProjectLifecycleState> = {
  // lowercase CRM stage IDs (canonical persisted form)
  'not-started': ProjectLifecycleState.ACTIVE,
  active: ProjectLifecycleState.ACTIVE,
  'on-hold': ProjectLifecycleState.ON_HOLD,
  completed: ProjectLifecycleState.CLOSED,
  canceled: ProjectLifecycleState.CLOSED,
  archived: ProjectLifecycleState.CLOSED,
  // legacy uppercase ProjectStatusEnum values still seen in some flows
  ACTIVE: ProjectLifecycleState.ACTIVE,
  NOT_STARTED: ProjectLifecycleState.ACTIVE,
  ON_HOLD: ProjectLifecycleState.ON_HOLD,
  COMPLETED: ProjectLifecycleState.CLOSED,
  CANCELED: ProjectLifecycleState.CLOSED,
  ARCHIVED: ProjectLifecycleState.CLOSED,
}

/**
 * Resolve a project's persisted status string to its semantic lifecycle state.
 * Returns `null` for unknown statuses so callers can decide whether to treat
 * unknown as active (default) or skip the row entirely.
 */
export function getProjectLifecycleState(
  status: string | null | undefined,
): ProjectLifecycleState | null {
  if (!status) return null
  return PROJECT_STATUS_LIFECYCLE_MAP[status] ?? null
}

/**
 * `true` when the project status maps to `CLOSED` (completed / canceled /
 * archived). Used by the allocation auto-archive hook and any other surface
 * that needs to ask "is this project done?" without re-listing closed strings.
 */
export function isClosedProjectStatus(
  status: string | null | undefined,
): boolean {
  return getProjectLifecycleState(status) === ProjectLifecycleState.CLOSED
}

/**
 * Reserved status ID for archived projects. Archived projects stay in the
 * database but are hidden from project lists, selectors, and cross-project
 * task views unless the caller explicitly asks for archived records.
 */
export const PROJECT_ARCHIVED_STATUS = 'archived'

/** Case-insensitive check covering both `'archived'` and legacy `'ARCHIVED'`. */
export function isArchivedProjectStatus(
  status: string | null | undefined,
): boolean {
  return (
    typeof status === 'string' &&
    status.toLowerCase() === PROJECT_ARCHIVED_STATUS
  )
}

/**
 * Shape stashed under `Project.metadata.archive` while a project is archived.
 * `previousStatus` lets unarchive restore the pre-archive status; `entityId`
 * is set when the archive cascaded from archiving the owning client so that
 * unarchiving the client restores exactly the projects it archived.
 */
export interface ProjectArchiveState {
  previousStatus: string
  archivedAt: string
  archivedBy?: string
  /** Set when this archive cascaded from the owning entity being archived. */
  entityId?: string
  /** Notes archived by this project's archive cascade; restored on unarchive. */
  noteIds?: string[]
}

// Project type enum
export enum ProjectTypeEnum {
  BASIC = 'BASIC',
  KANBAN = 'KANBAN',
  SCRUM = 'SCRUM',
  WATERFALL = 'WATERFALL',
}

// Project sort field enum
export enum ProjectSortFieldEnum {
  NAME = 'name',
  CODE = 'code',
  STATUS = 'status',
  CREATED_AT = 'createdAt',
  UPDATED_AT = 'updatedAt',
  START_DATE = 'startDate',
  END_DATE = 'endDate',
}

// Base project schema without refinement
const BaseProjectSchema = z.object({
  // Time logged on this project is billable by default (null = inherit from
  // the client, then the organization).
  defaultBillable: z.boolean().nullable().optional(),
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string(),
  code: z
    .string()
    .min(2)
    .max(3)
    .regex(/^[A-Z0-9]{2,3}$/, 'Code must be 2-3 uppercase letters or numbers'),
  description: z.string().nullable().optional(),
  logoUrl: z.string().optional(),
  color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .nullable()
    .optional(),
  status: z.string().default('active'), // Use lowercase CRM stage ID
  projectType: z.nativeEnum(ProjectTypeEnum).default(ProjectTypeEnum.BASIC),
  startDate: z.date().nullable().optional(),
  endDate: z.date().nullable().optional(),
  moduleType: z.string().nullable().optional(),
  moduleId: z.string().uuid().nullable().optional(),
  moduleName: z.string().nullable().optional(),
  modulePath: z.string().nullable().optional(),
  // Plan 4.5: formal entity FK (NULL = one-off project)
  entityId: z.string().uuid().nullable().optional(),
  rateCardId: z.string().uuid().nullable().optional(),
  entityName: z.string().nullable().optional(),
  entityLogoUrl: z.string().nullable().optional(),
  settings: z.any().default({}) as z.ZodType<ProjectSettings | any>,
  createdAt: z.date(),
  updatedAt: z.date(),
  customFields: z.record(z.string(), z.any()).optional(),
  isTemplate: z.boolean().default(false).optional(),
  templateId: z.string().uuid().nullable().optional(),
  _count: z
    .object({
      Task: z.number(),
      // Completed + canceled top-level tasks (the ones "Hide completed" hides).
      CompletedTask: z.number().optional(),
    })
    .optional(),
  // Open (not completed/canceled) task count. Populated only when the projects
  // list is requested with includeOpenTaskCount — used by the personal Lists
  // widgets to show a passive count without a per-row tasks fetch.
  openTaskCount: z.number().optional(),
  // Project members included for table display (readonly)
  projectMembers: z
    .array(
      z.object({
        id: z.string(),
        userId: z.string(),
        role: z.string(),
        status: z.string(),
        createdAt: z.date().or(z.string()).optional(),
        user: z.object({
          id: z.string(),
          displayName: z.string().nullable().optional(),
          firstName: z.string().nullable().optional(),
          lastName: z.string().nullable().optional(),
          email: z.string().nullable().optional(),
          image: z.string().nullable().optional(),
        }),
      }),
    )
    .optional(),
})

// Project schema with date validation
export const ProjectSchema = BaseProjectSchema.refine(
  data => {
    // Validate that end date is after start date
    if (data.startDate && data.endDate) {
      return data.endDate > data.startDate
    }
    return true
  },
  {
    message: 'End date must be after start date',
    path: ['endDate'],
  },
)

export type Project = z.infer<typeof ProjectSchema>

// Create project schema
export const CreateProjectSchema = BaseProjectSchema.omit({
  id: true,
  organizationId: true,
  createdAt: true,
  updatedAt: true,
  settings: true,
})
  .extend({
    settings: z.any().optional(),
    templateId: z.string().uuid().optional(),
    templateVariables: z.record(z.string(), z.any()).optional(),
    statusTemplateId: z.string().uuid().optional(),
    isTemplate: z.boolean().optional(),
  })
  .refine(
    data => {
      // Validate that end date is after start date
      if (data.startDate && data.endDate) {
        return data.endDate > data.startDate
      }
      return true
    },
    {
      message: 'End date must be after start date',
      path: ['endDate'],
    },
  )

export type CreateProject = z.infer<typeof CreateProjectSchema>

// Update project schema
export const UpdateProjectSchema = BaseProjectSchema.omit({
  id: true,
  organizationId: true,
  createdAt: true,
  updatedAt: true,
  settings: true,
})
  .extend({
    settings: z.any().optional(),
  })
  .partial()
  .refine(
    data => {
      // Validate that end date is after start date
      if (data.startDate && data.endDate) {
        return data.endDate > data.startDate
      }
      return true
    },
    {
      message: 'End date must be after start date',
      path: ['endDate'],
    },
  )

export type UpdateProject = z.infer<typeof UpdateProjectSchema>

// Project with tasks schema
export const ProjectWithTasksSchema = BaseProjectSchema.extend({
  tasks: z.array(z.any()).optional(),
})

export type ProjectWithTasks = z.infer<typeof ProjectWithTasksSchema>

// Project with members schema
export const ProjectWithMembersSchema = BaseProjectSchema.extend({
  members: z.array(z.any()).optional(),
})

export type ProjectWithMembers = z.infer<typeof ProjectWithMembersSchema>

// Project with statuses schema
export const ProjectWithStatusesSchema = BaseProjectSchema.extend({
  statuses: z.array(z.any()).optional(),
})

export type ProjectWithStatuses = z.infer<typeof ProjectWithStatusesSchema>

// Project with entity schema (legacy name kept for backward compatibility)
// Entity data is now resolved from moduleType/moduleId
export const ProjectWithEntitySchema = BaseProjectSchema

export type ProjectWithEntity = z.infer<typeof ProjectWithEntitySchema>

// Project response schema
export const ProjectResponseSchema = z.object({
  data: BaseProjectSchema,
  message: z.string().optional(),
})

export type ProjectResponse = z.infer<typeof ProjectResponseSchema>

// Projects response schema
export const ProjectsResponseSchema = z.object({
  data: z.array(BaseProjectSchema),
  total: z.number().optional(),
  message: z.string().optional(),
})

export type ProjectsResponse = z.infer<typeof ProjectsResponseSchema>

// Project configuration interfaces
export interface ProjectConfig {
  enableSprints: boolean
  enableKanbanBoard: boolean
  enableBacklog: boolean
  enableVelocity: boolean
  enableMilestones: boolean
  defaultView: 'list' | 'board' | 'calendar' | 'timeline'
  enableDependencies?: boolean
  enableGanttChart?: boolean
  requirePhaseGates?: boolean
  enableCriticalPath?: boolean
}

export interface ProjectGroupConfigs {
  groups: ProjectGroupConfig[]
  hasCustomGroups: boolean
  lastSyncedAt: Date
}

// Project group configuration
export interface ProjectGroupConfig {
  id: string
  name: string
  description: string
  color: string
  order: number
  isDefault: boolean
  groupType?: 'phase' | 'swimlane' | 'epic' | 'category'
  dependencies?: string[]
  allowParallelTasks?: boolean
  statusSource?: 'inherit' | 'template' | 'custom'
  statusTemplateId?: string
  customStatuses?: any
  settings: {
    isWaterfallPhase?: boolean
    requiresApproval?: boolean
    gatekeeper?: string
    isKanbanColumn?: boolean
    wipLimit?: number
    isEpic?: boolean
    storyPointsTarget?: number
  }
}

// Note: ProjectSettings interface moved to project-settings.ts

// Legacy project type configuration (for reference)
export interface ProjectTypeConfig {
  type: ProjectTypeEnum
  label: string
  description: string
  icon: string
  features: string[]
  settings: {
    enableSprints: boolean
    enableKanbanBoard: boolean
    enableBacklog: boolean
    enableVelocity: boolean
    enableMilestones: boolean
    defaultView: 'list' | 'board' | 'calendar' | 'timeline'
    wipLimits?: Record<string, number | null>
    enableDependencies?: boolean
    enableGanttChart?: boolean
    requirePhaseGates?: boolean
    enableCriticalPath?: boolean
  }
}

/**
 * Project type configurations
 * NOTE: For default status definitions, use the centralized status resolution in status-utils.ts
 * or DEFAULT_PROJECT_STATUS_CONFIGS from templates.ts for fallback scenarios
 */
export const PROJECT_TYPE_CONFIGS: Record<ProjectTypeEnum, ProjectTypeConfig> =
  {
    [ProjectTypeEnum.BASIC]: {
      type: ProjectTypeEnum.BASIC,
      label: 'Standard',
      description: 'Simple task management with standard workflow',
      icon: 'list',
      features: ['tasks', 'documents', 'comments'],
      settings: {
        enableSprints: false,
        enableKanbanBoard: false,
        enableBacklog: false,
        enableVelocity: false,
        enableMilestones: true,
        defaultView: 'list',
      },
    },
    [ProjectTypeEnum.KANBAN]: {
      type: ProjectTypeEnum.KANBAN,
      label: 'Kanban',
      description: 'Visual workflow with board view and WIP limits',
      icon: 'columns',
      features: ['kanban', 'tasks', 'documents', 'comments', 'wip-limits'],
      settings: {
        enableSprints: false,
        enableKanbanBoard: true,
        enableBacklog: true,
        enableVelocity: false,
        enableMilestones: true,
        defaultView: 'board',
        wipLimits: {
          Ready: null,
          IN_PROGRESS: 5,
          Review: 3,
        },
      },
    },
    [ProjectTypeEnum.SCRUM]: {
      type: ProjectTypeEnum.SCRUM,
      label: 'Scrum',
      description: 'Sprint-based development with velocity tracking',
      icon: 'zap',
      features: [
        'sprints',
        'backlog',
        'velocity',
        'kanban',
        'tasks',
        'story-points',
      ],
      settings: {
        enableSprints: true,
        enableKanbanBoard: true,
        enableBacklog: true,
        enableVelocity: true,
        enableMilestones: true,
        defaultView: 'board',
      },
    },
    [ProjectTypeEnum.WATERFALL]: {
      type: ProjectTypeEnum.WATERFALL,
      label: 'Waterfall',
      description: 'Sequential phases with dependencies and milestones',
      icon: 'trending-down',
      features: ['phases', 'milestones', 'dependencies', 'gantt', 'tasks'],
      settings: {
        enableSprints: false,
        enableKanbanBoard: false,
        enableBacklog: false,
        enableVelocity: false,
        enableMilestones: true,
        defaultView: 'list',
        enableDependencies: true,
        enableGanttChart: true,
        requirePhaseGates: true,
        enableCriticalPath: true,
      },
    },
  }

// =====================
// Template Types
// =====================

/**
 * Options for saving a project as a template
 */
export interface SaveAsTemplateOptions {
  /** Name for the new template */
  name: string
  /** Optional description for the template */
  description?: string
  /** Copy project members to template */
  copyMembers?: boolean
  /** Copy task assignees to template */
  copyAssignments?: boolean
  /** Copy task dates to template */
  copyDates?: boolean
  /** Copy cycle templates to template */
  copyCycles?: boolean
}

/**
 * Options for creating a project from a template
 */
export interface CreateFromTemplateOptions {
  /** Copy project members from template */
  copyMembers?: boolean
  /** Copy task assignees from template */
  copyAssignments?: boolean
  /** Copy task dates from template */
  copyDates?: boolean
  /** Copy sprint structure from template */
  copySprintStructure?: boolean
  /** Copy task groups from template */
  copyTaskGroups?: boolean
  /** Copy tasks from template */
  copyTasks?: boolean
  /** Copy documents from template */
  copyDocuments?: boolean
  /** Copy notes from template */
  copyNotes?: boolean
  /** Copy cycle templates from template */
  copyCycles?: boolean
}

/**
 * Default options for creating a project from a template
 */
export const DEFAULT_CREATE_FROM_TEMPLATE_OPTIONS: CreateFromTemplateOptions = {
  copyMembers: true,
  copyAssignments: true,
  copyDates: true,
  copySprintStructure: true,
  copyTaskGroups: true,
  copyTasks: true,
  copyDocuments: true,
  copyNotes: true,
  copyCycles: true,
}

/**
 * DTO for creating a project from a template
 */
export interface CreateFromTemplateDto {
  /** Name for the new project */
  name: string
  /** Optional project code */
  code?: string
  /** Optional description */
  description?: string
  /** Optional module type to associate project with (e.g., ENTITY, DEAL) */
  moduleType?: string
  /** Optional module ID to associate project with */
  moduleId?: string
  /** Optional start date */
  startDate?: Date | string
  /** Optional end date */
  endDate?: Date | string
  /** Start date for cycles - used to calculate first cycle period */
  cycleStartDate?: Date | string
  /** Copy options */
  options?: CreateFromTemplateOptions
}
